import { useEffect, useRef, useState } from 'react'

import './App.scss'
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';

import LoadingPage from './pages/loading/loading-page.page'
import Register from './pages/sign-in/register.component';
import ProfilePage from './pages/profile/profile.page';
import SignIn from './pages/sign-in/sign-in.component';
import LogoutPage from './pages/logout/logout-page.page';
import PageNotFound from './pages/page-not-found/page-not-found.component';
import LandingPage from './pages/landing-page/landing-page.page';
import HomePage from './pages/home-page/home-page.page';
import ManageGamePage from './pages/manage-game/manage-game.page';
import JoinPage from './pages/join-page/join-page.page';
import AdminPage from './pages/admin/admin.page';
import PlayGamePage from './pages/play-game/play-game.page';
import ChangeLog from './pages/changelog/changelog.page';
import { screenSizes } from './utils/responsiveQueries';
import { setScreenSize } from './state/slices/contentSlice';
import LeaderboardPage from './pages/leaderboard/leaderboard.page';
import {
  getCollectionFromDb,
  getGameDataFromDb,
  getRegsFromDb,
  getUniqueDocumentFromDb,
  getUserMembershipFromDb
} from './utils/getDataFromDb';
import { setMembership } from './state/slices/userSlice';
import PricingPage from './pages/pricing/pricing-page.page';
import QuotePage from './pages/quote/quote.page';
import MyPlan from './pages/my-plan/my-plan.component';
import PrintLogins from './components/MANAGE-GAME/players/print-logins.component';
import TermsOfService from './pages/info/terms-of-service.component';
import PrivacyPolicy from './pages/info/privacy-policy.component';
import Coppa from './pages/info/coppa.component';
import MyOrgs from './components/HOME/orgs/my-orgs.page';
import UnderConstruction from './pages/under-construction/under-construction.page';
import ReactGA from "react-ga4";
import ForgotPasswordPage from './pages/sign-in/forgot-password.page';
import VerifyEmailPage from './pages/sign-in/verify-email.page';
import StripeContainer from './pages/payment-stripe/payment-container.component';
import PaymentReturn from './pages/payment-stripe/payment-return.component';
import PrintQrCodes from './components/MANAGE-GAME/players/print-qr-codes.component';
import PrintSecretCodes from './components/MANAGE-GAME/create-elements/secret-codes/print-secret-codes.component';
import PreviewLandingPage from './components/PREVIEW/preview-landing-page/preview-landing-page.component';
import ResourcesPage from './pages/resources/resources.page';
import MembershipDetails from './pages/membership/membership-details.component';
import PrintLootKeys from './components/MANAGE-GAME/create-elements/loot-keys/print-loot-keys.component';
import { createListenerSession } from './utils/listenerSession';

const gaTrackingID = "G-BMG0E5NLFS";

function App() {

  const currentUser = useSelector(state => state.userData.currentUser);
  const userProfile = useSelector(state => state.userData.userProfile);
  const [managingList, setManagingList] = useState({});
  const [enrolledList, setEnrolledList] = useState({});
  const [archivedList, setArchivedList] = useState({});
  const [orgs, setOrgs] = useState(null);
  // the user's own membership doc, wrapped as { data } so "not received yet"
  // (null) is distinct from "received, no doc" ({ data: undefined }). the
  // effect below decides whether this or an org membership is dispatched.
  const [personalMembership, setPersonalMembership] = useState(null);
  const orgsRef = useRef();
  const manageRef = useRef();
  const enrollRef = useRef();
  const archiveRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // primitives so the listener effects only re-run when the signed-in user
  // actually changes, not every time the userProfile object is replaced.
  const appId = userProfile?.appId;
  const hasUser = !!currentUser;
  const canHaveMembership = !!currentUser?.email &&
    !currentUser.email.includes('@gamablyplayers.com');

  useEffect(() => {
    // navigate('/construction');
    // return;

    const handleResize = () => getScreenSize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [])

  // registrations → managingList / enrolledList / archivedList. closing this
  // on logout is what stops a previous user's games from reappearing.
  useEffect(() => {
    if (!hasUser || !appId) {
      manageRef.current = {};
      setManagingList(manageRef.current);
      enrollRef.current = {};
      setEnrolledList(enrollRef.current);
      archiveRef.current = {};
      setArchivedList(archiveRef.current);
      return;
    }
    if (location.pathname.includes('/preview/')) return;
    const session = createListenerSession();
    getRegs(appId, session);
    return () => session.close();
  }, [hasUser, appId])

  // the user's membership doc and their orgs, opened once per signed-in user.
  useEffect(() => {
    if (!hasUser || !appId || !canHaveMembership) {
      orgsRef.current = null;
      setOrgs(orgsRef.current);
      setPersonalMembership(null);
      return;
    }
    if (location.pathname.includes('/preview/')) return;
    const session = createListenerSession();
    getMembership(appId, session);
    getOrgs(appId, session);
    return () => session.close();
  }, [hasUser, appId, canHaveMembership])

  // an org membership outranks the user's own plan. deciding here, from both
  // inputs, means whichever listener fires last can't overwrite the other.
  useEffect(() => {
    if (!personalMembership) return;
    const orgList = Object.values(orgs ?? {});
    if (orgList.length === 0) {
      dispatch(setMembership(personalMembership.data));
      return;
    }
    let firstOrg = orgList
      .sort((a, b) => a.ts_expires > b.ts_expires ? 1 : -1)[0]
    const memOrg = {
      'appId': userProfile.appId,
      'email': userProfile.email,
      'membership': 3,
      'ts': firstOrg.dateJoin,
      'ts_expires': firstOrg.dateExpire
    }
    dispatch(setMembership({ ...memOrg }))
  }, [personalMembership, orgs])

  function getRegs(appId, session) {
    // gets registration data for user, then gets game data for each registered game
    // also breaks data up into managing and playing
    // creates listener for reg changes - not game data changes
    getRegsFromDb({
      'appId': appId,
      // 'status': 'active',
      'callback': callback,
      'handleListener': session.track
    })
    function callback(regData) {
      let currentManaging = { ...manageRef.current };
      let currentEnrolled = { ...enrollRef.current };
      if (regData.status === 'archived') {
        archiveRef.current = {
          ...archiveRef.current,
          ...{ [regData.gameId]: regData }
        };
        setArchivedList(archiveRef.current);
        if (currentManaging[regData.gameId]) {
          delete currentManaging[regData.gameId];
          manageRef.current = { ...currentManaging };
          setManagingList(manageRef.current);
        }
        return;
      } else if (archiveRef.current?.[regData.gameId]) {
        // copy before deleting so state gets a new object and React re-renders
        let currentArchived = { ...archiveRef.current };
        delete currentArchived[regData.gameId];
        archiveRef.current = currentArchived;
        setArchivedList(archiveRef.current);
      }
      if (
        !regData.roles[appId] ||
        (
          currentManaging[regData.gameId] &&
          regData.roles[appId] &&
          regData.roles[appId].length === 1 &&
          regData.roles[appId].includes('player')
        )
      ) {
        if (currentManaging[regData.gameId]) {
          delete currentManaging[regData.gameId];
          manageRef.current = { ...currentManaging };
          setManagingList(manageRef.current);
        }
        return;
      }
      if (
        !regData.roles[appId] ||
        (
          regData.roles[appId] &&
          currentEnrolled[regData.gameId] &&
          !regData.roles[appId].includes('player')
        )
      ) {
        if (currentEnrolled[regData.gameId]) {
          delete currentEnrolled[regData.gameId];
          enrollRef.current = { ...currentEnrolled };
          setEnrolledList(enrollRef.current);
        }
        return;
      }
      getGameDataFromDb({
        'gameId': regData.gameId,
        'appId': appId,
        'once': true,
        'callback': callback2
      });
      function callback2(gameData) {
        // a one-time read that can land after logout; drop it so the previous
        // user's game isn't written back into the lists
        if (!session.active) return;
        let tempGameData = { ...gameData };
        tempGameData.roles = regData.roles[appId];
        tempGameData.lastEdit = regData.lastEdit;
        if (tempGameData.roles.includes('owner') || tempGameData.roles.includes('manager') || tempGameData.roles.includes('distributor')) {
          let newGame = { ...tempGameData };
          newGame.role = newGame.roles.filter(r => r !== 'player')[0];
          manageRef.current = { ...manageRef.current, ...{ [newGame.gameId]: newGame } }
          setManagingList(manageRef.current);
        }
        if (tempGameData.roles.includes('player')) {
          let newPlayerGame = { ...tempGameData };
          newPlayerGame.role = 'player'
          enrollRef.current = { ...enrollRef.current, ...{ [newPlayerGame.gameId]: newPlayerGame } }
          setEnrolledList(enrollRef.current);
        }
      }
    }
  }

  function getMembership(appId, session) {
    console.log('getting membership for', appId)
    getUserMembershipFromDb({
      'appId': appId,
      'callback': callback,
      'handleListener': session.track
    })
    function callback(data) {
      setPersonalMembership({ data });
    }
  }

  // orgRegs says which orgs the user belongs to (and their roles); each org
  // gets one doc listener. every orgRegs snapshot is the full membership list,
  // so an org missing from it has removed the user — its listener is closed
  // and it drops out of orgs right away.
  function getOrgs(appId, session) {
    const orgListeners = new Map();
    let orgRoles = {};
    session.track(() => {
      orgListeners.forEach(unsub => unsub());
      orgListeners.clear();
    });

    getCollectionFromDb({
      'collection': 'orgRegs',
      'query1': {
        'key': 'list',
        'condition': 'array-contains',
        'value': appId
      },
      'snapshotCallback': handleOrgRegs,
      'handleListener': session.track
    })

    function handleOrgRegs(regsById) {
      if (!session.active) return;
      orgRoles = {};
      for (const [orgId, reg] of Object.entries(regsById)) {
        orgRoles[orgId] = reg.roles;
      }

      // keep only orgs still in the snapshot, with their current roles
      const nextOrgs = {};
      for (const [orgId, org] of Object.entries(orgsRef.current ?? {})) {
        if (orgRoles[orgId]) nextOrgs[orgId] = { ...org, roles: orgRoles[orgId] };
      }
      orgListeners.forEach((unsub, orgId) => {
        if (orgRoles[orgId]) return;
        unsub();
        orgListeners.delete(orgId);
      });
      // null, not {}, when there are none: the collaborator screens read a
      // truthy orgs as "this user belongs to an org"
      orgsRef.current = Object.keys(nextOrgs).length > 0 ? nextOrgs : null;
      setOrgs(orgsRef.current);

      for (const orgId of Object.keys(orgRoles)) {
        if (orgListeners.has(orgId)) continue;
        getUniqueDocumentFromDb({
          'collection': 'orgs',
          'docId': orgId,
          'callback': handleOrgDoc,
          'handleListener': unsub => {
            if (session.active) orgListeners.set(orgId, unsub);
            else unsub();
          }
        })
      }
    }

    function handleOrgDoc(data, orgId) {
      if (!session.active || !data || !orgRoles[orgId]) return;
      orgsRef.current = {
        ...orgsRef.current,
        ...{ [orgId]: { ...data, roles: orgRoles[orgId] } }
      }
      setOrgs(orgsRef.current);
    }
  }

  useEffect(() => {
    if (!screenSizes) { return; }
    getScreenSize();
  }, [screenSizes, location])

  function getScreenSize() {
    let sizeArr = screenSizes.filter(s => window.innerWidth >= s.minWidth && window.innerWidth <= s.maxWidth);
    let size;
    if (!sizeArr || !sizeArr.length) {
      size = 'desktop'
    } else {
      size = sizeArr[0].name;
    }
    dispatch(setScreenSize(size));
  }

  return (
    <div>
      <Routes>
        <Route path='' element={<LandingPage />}></Route>
        <Route path='/signin' element={<SignIn />}></Route>
        <Route path='/signin/:joinCode' element={<SignIn />}></Route>
        <Route path='/forgotpassword' element={<ForgotPasswordPage />}></Route>
        <Route path='/register' element={<Register />}></Route>
        <Route path='/register/:joinCode' element={<Register />}></Route>
        <Route path='/verifyemail' element={<VerifyEmailPage />}></Route>
        <Route path='/logout' element={<LogoutPage />}></Route>
        <Route path='/loading' element={<LoadingPage />}></Route>
        <Route path='/loading/:joinCode' element={<LoadingPage />}></Route>
        <Route path='/profile' element={<ProfilePage />}></Route>
        <Route path='/profile/:joinCode' element={<ProfilePage />}></Route>
        <Route path='/home/*' element={
          <HomePage
            orgs={orgs}
            managingList={managingList}
            enrolledList={enrolledList}
            archivedList={archivedList} />
        }></Route>
        <Route path='/resources' element={
          <ResourcesPage />
        }></Route>
        <Route path='/membership' element={
          <MembershipDetails />
        }></Route>
        <Route path='/orgs/*' element={<MyOrgs orgs={orgs} />}></Route>
        <Route path='/join' element={<JoinPage />}></Route>
        <Route path='/join/:joinCode/*' element={<JoinPage />}></Route>
        <Route path='/manage/:gamePath/*' element={
          <ManageGamePage
            orgs={orgs}
            managingList={managingList} />
        }></Route>
        <Route path='/play/:gamePath/*' element={
          <PlayGamePage
            enrolledList={enrolledList} />
        }></Route>
        <Route path='/preview/*' element={
          <PreviewLandingPage />
        }></Route>
        <Route path='/plan' element={<MyPlan />}></Route>
        <Route path='/printlogins/:gamePath'
          element={<PrintLogins />}
        />
        <Route path='/printqrcodes/:gamePath' element={
          <PrintQrCodes />
        }></Route>
        <Route path='/printsecretcodes/:gamePath' element={
          <PrintSecretCodes />
        }></Route>
        <Route path='/printlootkeys' element={
          <PrintLootKeys />
        }></Route>
        <Route path='/admin/*' element={<AdminPage />}></Route>
        <Route path='/changelog' element={<ChangeLog />}></Route>
        <Route path='/live/:livePath' element={<LeaderboardPage />}></Route>
        <Route path='/pricing' element={<PricingPage />}></Route>
        <Route path='/purchase/:product' element={
          <StripeContainer />
        }></Route>
        <Route path='/payment-return/:checkout_session_id' element={
          <PaymentReturn />
        }></Route>
        <Route path='/quote' element={<QuotePage />}></Route>
        <Route path='/terms' element={<TermsOfService />}></Route>
        <Route path='/privacy' element={<PrivacyPolicy />}></Route>
        <Route path='/coppa' element={<Coppa />}></Route>
        <Route path='/404' element={<PageNotFound />}></Route>
        <Route path='/construction' element={<UnderConstruction />}></Route>
        <Route path="*" element={<Navigate replace to="/404" />} />
        <Route path="/noaccess" element={<Navigate replace to="/404" />} />
      </Routes>

    </div>
  );
}

export default App
